import * as Yup from 'yup';
import {
  SORTBY_OPTIONS,
  industryInvolvements,
  referrals
} from '../../utils/constants';
import { useEffect, useState } from 'react';
import Accessibilities from './Accessibilities';
import { FiAlertTriangle } from 'react-icons/fi';
import { Form } from 'react-bootstrap';
import HasChildren from './HasChildren';
import ReferralEmail from './ReferralEmail';
import SelectMulti from '../SelectMulti/SelectMulti';
import { createLocalizedOptions } from '../../pages/root/App';
import iconHuman from '../../assets/images/ubisoft-playtest-icon-human-black.webp';
import messages from './messages';
import siteMessages from '../../pages/root/siteMessages';
import { useIntl } from 'react-intl';

interface Props {
  values: any;
  errors: any;
  setFieldValue: any;
  updateSchema: (value: any) => void;
}

const AboutYou = ({ values, errors, setFieldValue, updateSchema }: Props) => {
  const intl = useIntl();
  const industryInvolvementsOptions = createLocalizedOptions(
    industryInvolvements,
    messages,
    SORTBY_OPTIONS.Name
  );
  const referralsOptions = createLocalizedOptions(referrals, messages);
  const [aboutYouSchema, setAboutYouSchema] = useState<any>();
  const [hasChildrenSchema, setHasChildrenSchema] = useState<any>();
  const [accessibilitiesSchema, setAccessibilitiesSchema] = useState<any>();
  const [referralEmailSchema, setReferralEmailSchema] = useState<any>();

  useEffect(() => {
    const aboutYouSchema = {
      ...hasChildrenSchema,
      ...accessibilitiesSchema,
      ...referralEmailSchema,
      industryInvolvementIds: Yup.array(Yup.number()).min(
        1,
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      referralIds: Yup.array(Yup.number()).min(
        1,
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      )
    };
    setAboutYouSchema(aboutYouSchema);
  }, [hasChildrenSchema, accessibilitiesSchema, referralEmailSchema]);

  useEffect(() => {
    updateSchema(aboutYouSchema);
  }, [aboutYouSchema]);

  return (
    <div className="about-you">
      <div className="header">
        <img src={iconHuman} alt="" />
        <h3>{intl.formatMessage({ ...messages.headerAboutYou })}</h3>
      </div>
      <div className="content">
        <HasChildren
          value={values.hasChildren}
          error={errors.hasChildren}
          setFieldValue={setFieldValue}
          updateSchema={(schema) => setHasChildrenSchema(schema)}
        />
        <Form.Group controlId="industryInvolvementIds">
          <Form.Label className="fw-bold">
            {intl.formatMessage({ ...messages.industryInvolvements })}
          </Form.Label>
          <SelectMulti
            className={errors.industryInvolvementIds ? 'invalid-select' : ''}
            id="industryInvolvementIds"
            isMulti={true}
            options={industryInvolvementsOptions}
            value={values.industryInvolvementIds}
            onChange={(value: any) =>
              setFieldValue('industryInvolvementIds', value)
            }
            placeholder={intl.formatMessage({
              ...siteMessages.selectMultiPlaceholder
            })}
          />
          {errors.industryInvolvementIds && (
            <div className="feedback">
              <FiAlertTriangle className="mb-1 me-1" />
              {errors.industryInvolvementIds}
            </div>
          )}
        </Form.Group>
        <Form.Group controlId="referralIds">
          <Form.Label className="fw-bold">
            {intl.formatMessage({ ...messages.referrals })}
          </Form.Label>
          <SelectMulti
            className={errors.referralIds ? 'invalid-select' : ''}
            id="referralIds"
            isMulti={true}
            options={referralsOptions}
            value={values.referralIds}
            onChange={(value: any) => setFieldValue('referralIds', value)}
            placeholder={intl.formatMessage({
              ...siteMessages.selectMultiPlaceholder
            })}
          />
          {errors.referralIds && (
            <div className="feedback">
              <FiAlertTriangle className="mb-1 me-1" />
              {errors.referralIds}
            </div>
          )}
        </Form.Group>
        <ReferralEmail
          values={values}
          errors={errors}
          setFieldValue={setFieldValue}
          updateSchema={(schema) => setReferralEmailSchema(schema)}
        />
        <Accessibilities
          values={values}
          errors={errors}
          setFieldValue={setFieldValue}
          updateSchema={(schema) => setAccessibilitiesSchema(schema)}
        />
      </div>
    </div>
  );
};

export default AboutYou;
